import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
});

const TOKEN_KEY = "xpro_admin_token";
const USER_TOKEN_KEY = "xpro_user_token";

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

api.interceptors.request.use((config) => {
  const url = config.url || "";
  const adminToken = localStorage.getItem(TOKEN_KEY);
  const userToken  = localStorage.getItem(USER_TOKEN_KEY);

  // Admin routes always use the admin token.
  // All other routes prefer the user token (checkout, evolution, etc.)
  const isAdminPath = url.startsWith("/admin") || url === "/auth/login" || url === "/auth/me";
  const t = isAdminPath ? adminToken : (userToken || adminToken);

  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      tokenStore.clear();
      // Don't clear user token here - let UserAuthContext handle it
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Ha ocurrido un error. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const foundersApi = {
  stats: () => api.get("/founders/stats").then((r) => r.data),
  register: (payload) => api.post("/founders/register", payload).then((r) => r.data),
};

export const authApi = {
  login: (email, password) =>
    api.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
};

export const adminApi = {
  list: () => api.get("/admin/founders").then((r) => r.data),
  remove: (id) => api.delete(`/admin/founders/${id}`).then((r) => r.data),
  stats: () => api.get("/admin/stats").then((r) => r.data),
  emailPreview: (pack) => api.get(`/admin/email-preview?pack=${pack}`).then((r) => r.data),
  sendEmail: (pack, subject, body) =>
    api.post("/admin/send-email", { pack, subject, body }).then((r) => r.data),
  sendTestEmail: (pack, subject, body) =>
    api.post("/admin/send-test-email", { pack, subject, body }).then((r) => r.data),
  // Users (Socios)
  listUsers: () => api.get("/admin/usuarios").then((r) => r.data),
  getUserDetail: (userId) => api.get(`/admin/usuarios/${userId}`).then((r) => r.data),
  createUser: (data) => api.post("/admin/usuarios/crear", data).then((r) => r.data),
  reinviteUser: (id) => api.post(`/admin/usuarios/${id}/reinvitar`).then((r) => r.data),
  activateUser: (id) => api.post(`/admin/usuarios/${id}/activar`).then((r) => r.data),
  assignRoutine: (userId, routineId) =>
    api.post(`/admin/usuarios/${userId}/asignar-rutina`, { routine_id: routineId }).then((r) => r.data),
  getUserEvolution: (userId) => api.get(`/admin/usuarios/${userId}/evolution`).then((r) => r.data),
  addUserEvolution: (userId, data) => api.post(`/admin/usuarios/${userId}/evolution`, data).then((r) => r.data),
  updateUser: (id, data) => api.patch(`/admin/usuarios/${id}`, data).then((r) => r.data),
  deleteUser: (id) => api.delete(`/admin/usuarios/${id}`).then((r) => r.data),
  deactivateUser: (id) => api.post(`/admin/usuarios/${id}/desactivar`).then((r) => r.data),
  reactivateUser: (id) => api.post(`/admin/usuarios/${id}/reactivar`).then((r) => r.data),
  importarSocios: (formData) =>
    api.post("/admin/socios/importar", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  reoptimizarFotos: () => api.post("/admin/maquinas/reoptimizar-fotos").then((r) => r.data),
  reoptimizarFotosEstado: () => api.get("/admin/maquinas/reoptimizar-fotos/estado").then((r) => r.data),
  // Routines
  listRoutines: () => api.get("/admin/rutinas").then((r) => r.data),
  createRoutine: (data) => api.post("/admin/rutinas", data).then((r) => r.data),
  updateRoutine: (id, data) => api.put(`/admin/rutinas/${id}`, data).then((r) => r.data),
  deleteRoutine: (id) => api.delete(`/admin/rutinas/${id}`).then((r) => r.data),
  deleteAllRoutines: () => api.delete("/admin/rutinas").then((r) => r.data),
  // Subscriptions
  listSubscriptions: (status) => api.get(`/admin/suscripciones${status ? `?status=${status}` : ""}`).then((r) => r.data),
  updateSubNotes: (id, notas) => api.put(`/admin/suscripciones/${id}/notas`, { notas }).then((r) => r.data),
  cancelSubscription: (id) => api.post(`/admin/suscripciones/${id}/cancelar`).then((r) => r.data),
};

export const subscriptionsApi = {
  list: () => api.get("/mis-suscripciones").then((r) => r.data),
  cancel: (id) => api.post(`/mis-suscripciones/${id}/cancelar`).then((r) => r.data),
};

export const accesoriosApi = {
  list: () => api.get("/accesorios").then((r) => r.data),
  adminList: () => api.get("/admin/accesorios").then((r) => r.data),
  create: (data) => api.post("/admin/accesorios", data).then((r) => r.data),
  update: (id, data) => api.put(`/admin/accesorios/${id}`, data).then((r) => r.data),
  delete: (id) => api.delete(`/admin/accesorios/${id}`).then((r) => r.data),
};

export const machinesApi = {
  list: () => api.get("/maquinas").then((r) => r.data),
  getExercises: (numero) => api.get(`/maquinas/${encodeURIComponent(numero)}/ejercicios`).then((r) => r.data),
  adminList: () => api.get("/admin/maquinas").then((r) => r.data),
  create: (data) => api.post("/admin/maquinas", data).then((r) => r.data),
  update: (numero, data) => api.put(`/admin/maquinas/${numero}`, data).then((r) => r.data),
  deleteAll: () => api.delete("/admin/maquinas").then((r) => r.data),
  importCsv: (file) => {
    const form = new FormData();
    form.append("file", file);
    return api.post("/admin/maquinas/importar", form).then((r) => r.data);
  },
  downloadQrSheet: async (baseUrl) => {
    const token = tokenStore.get();
    const qs = baseUrl ? `?base_url=${encodeURIComponent(baseUrl)}` : "";
    const res = await fetch(`${API_BASE}/admin/maquinas/qr-sheet${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Error generando el PDF");
    }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "xpro-qr-maquinas.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  },
};

export const zonasApi = {
  list: () => api.get("/admin/zonas").then((r) => r.data),
  create: (data) => api.post("/admin/zonas", data).then((r) => r.data),
  update: (codigo, data) => api.put(`/admin/zonas/${codigo}`, data).then((r) => r.data),
  delete: (codigo) => api.delete(`/admin/zonas/${codigo}`).then((r) => r.data),
};

export const myRoutinesApi = {
  getActive: () => api.get("/mis-rutinas/activa").then((r) => r.data),
  list: () => api.get("/mis-rutinas").then((r) => r.data),
  create: (data) => api.post("/mis-rutinas", data).then((r) => r.data),
  update: (id, data) => api.put(`/mis-rutinas/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/mis-rutinas/${id}`).then((r) => r.data),
};

export default api;

export const ejerciciosApi = {
  list: (dificultad) =>
    api.get(dificultad != null ? `/ejercicios?dificultad=${dificultad}` : "/ejercicios").then((r) => r.data),
  adminList: (numero_maquina) =>
    api.get(numero_maquina ? `/admin/ejercicios?numero_maquina=${encodeURIComponent(numero_maquina)}` : "/admin/ejercicios").then((r) => r.data),
  create: (data) => api.post("/admin/ejercicios", data).then((r) => r.data),
  update: (id, data) => api.put(`/admin/ejercicios/${id}`, data).then((r) => r.data),
  deactivate: (id) => api.delete(`/admin/ejercicios/${id}`).then((r) => r.data),
  deleteAll: () => api.delete("/admin/ejercicios").then((r) => r.data),
  hardDelete: (id) => api.delete(`/admin/ejercicios/${id}/eliminar`).then((r) => r.data),
};
